import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { Grid } from '@material-ui/core';
import { cancelarAlert } from 'app/services/alerts';
import { calcularValorComDesconto } from 'app/utils/monetarioUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect } from 'react';
import { MdSettings } from 'react-icons/md';
import * as yup from 'yup';
import { mergeServicoFiscalVenda } from 'app/main/venda/components/VendaProdutoServico/cadastro/servicoFiscalVendaUtils';
import TabDadosFiscaisServico from './TabDadosFiscaisServico';

const mergeServicoFiscalInicial = (itemServico, produtoServico, origemVenda) => {
  if (origemVenda) {
    const fiscalServico =
      produtoServico?.servico?.fiscal || produtoServico?.produtoServico?.servicoFiscal || {};
    const fiscalOverride = produtoServico?.servicoFiscal || {};

    return mergeServicoFiscalVenda(fiscalServico, fiscalOverride);
  }

  const fiscalServico = itemServico?.servico?.fiscal || {};
  const fiscalOverride = itemServico?.servicoFiscal || {};

  return mergeServicoFiscalVenda(fiscalServico, fiscalOverride);
};

const RetencaoAliquotaModal = ({
  modalRetencaoAliquota,
  setModalRetencaoAliquota,
  onSubmit,
  produtoServico,
  itemServico,
  origemVenda = false,
}) => {
  const buildInitialValues = () => {
    const fiscal = mergeServicoFiscalInicial(itemServico, produtoServico, origemVenda);

    return {
      id: fiscal?.id,

      cnaeId: fiscal?.cnaeId ?? fiscal?.cnae?.id,
      lc116Id: fiscal?.lc116Id ?? fiscal?.lc116?.id,
      lc116Convertido: fiscal?.lc116Convertido,
      codigoServicoMunicipal: fiscal?.codigoServicoMunicipal,

      tipoIss: fiscal?.tipoIss ?? 'PERCENTUAL',
      valorIss: fiscal?.valorIss,
      issRetidoNaFonte: fiscal?.issRetidoNaFonte || false,

      tipoPis: fiscal?.tipoPis ?? 'PERCENTUAL',
      valorPis: fiscal?.valorPis,
      pisRetidoNaFonte: fiscal?.pisRetidoNaFonte || false,

      tipoCofins: fiscal?.tipoCofins ?? 'PERCENTUAL',
      valorCofins: fiscal?.valorCofins,
      cofinsRetidoNaFonte: fiscal?.cofinsRetidoNaFonte || false,

      tipoIrrf: fiscal?.tipoIrrf ?? 'PERCENTUAL',
      valorIrrf: fiscal?.valorIrrf,
      irrfRetidoNaFonte: fiscal?.irrfRetidoNaFonte || false,

      tipoInss: fiscal?.tipoInss ?? 'PERCENTUAL',
      valorInss: fiscal?.valorInss,
      inssRetidoNaFonte: fiscal?.inssRetidoNaFonte || false,

      tipoCsll: fiscal?.tipoCsll ?? 'PERCENTUAL',
      valorCsll: fiscal?.valorCsll,
      csllRetidoNaFonte: fiscal?.csllRetidoNaFonte || false,

      tipoIbs: fiscal?.tipoIbs ?? 'PERCENTUAL',
      valorIbs: fiscal?.valorIbs,
      ibsRetidoNaFonte: fiscal?.ibsRetidoNaFonte || false,

      tipoIbsMunicipal: fiscal?.tipoIbsMunicipal ?? 'PERCENTUAL',
      valorIbsMunicipal: fiscal?.valorIbsMunicipal,

      tipoCbs: fiscal?.tipoCbs ?? 'PERCENTUAL',
      valorCbs: fiscal?.valorCbs,
      cbsRetidoNaFonte: fiscal?.cbsRetidoNaFonte || false,

      tipoIs: fiscal?.tipoIs ?? 'PERCENTUAL',
      valorIs: fiscal?.valorIs,
      isRetidoNaFonte: fiscal?.isRetidoNaFonte || false,

      cstSituacaoTributaria: fiscal?.cstSituacaoTributaria,
      cstClassificacaoTributaria: fiscal?.cstClassificacaoTributaria,
    };
  };

  const initialValues = buildInitialValues();

  /**
   * Base para validar retenção em R$: na venda usa o valor da linha; no contrato usa o valor
   * da linha do contrato (itemServico.valor), não o valorVenda do cadastro do serviço.
   */
  const getValorBaseRetencao = () => {
    if (origemVenda) {
      return produtoServico?.valor;
    }
    if (itemServico?.valor != null && itemServico.valor !== '') {
      return Number(itemServico.valor);
    }
    return itemServico?.servico?.valorVenda;
  };

  const isRetencaoValorValida = (tipo, retidoNaFonte, valorImposto) => {
    const valorBase = getValorBaseRetencao();
    if (tipo !== 'VALOR' || !retidoNaFonte || !valorBase) {
      return true;
    }
    return calcularValorComDesconto(tipo, valorBase, valorImposto) >= 0;
  };

  const RetencaoAliquotaSchema = yup.object().shape({
    valorIss: yup
      .string()
      .nullable()
      .test('notNegativeValorIss', 'O valor do ISS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoIss,
          formik.values?.issRetidoNaFonte,
          formik.values?.valorIss
        );
      }),
    valorIrrf: yup
      .string()
      .nullable()
      .test('notNegativeValorIrrf', 'O valor do IRRF não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoIrrf,
          formik.values?.irrfRetidoNaFonte,
          formik.values?.valorIrrf
        );
      }),
    valorPis: yup
      .string()
      .nullable()
      .test('notNegativeValorPis', 'O valor do PIS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoPis,
          formik.values?.pisRetidoNaFonte,
          formik.values?.valorPis
        );
      }),
    valorCofins: yup
      .string()
      .nullable()
      .test('notNegativeValorCofins', 'O valor do COFINS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoCofins,
          formik.values?.cofinsRetidoNaFonte,
          formik.values?.valorCofins
        );
      }),
    valorInss: yup
      .string()
      .nullable()
      .test('notNegativeValorInss', 'O valor do INSS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoInss,
          formik.values?.inssRetidoNaFonte,
          formik.values?.valorInss
        );
      }),
    valorCsll: yup
      .string()
      .nullable()
      .test('notNegativeValorCsll', 'O valor do CSLL não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoCsll,
          formik.values?.csllRetidoNaFonte,
          formik.values?.valorCsll
        );
      }),
    valorIbs: yup
      .string()
      .nullable()
      .test('notNegativeValorIbs', 'O valor do IBS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoIbs,
          formik.values?.ibsRetidoNaFonte,
          formik.values?.valorIbs
        );
      }),
    valorCbs: yup
      .string()
      .nullable()
      .test('notNegativeValorCbs', 'O valor do CBS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(
          formik.values?.tipoCbs,
          formik.values?.cbsRetidoNaFonte,
          formik.values?.valorCbs
        );
      }),
    valorIs: yup
      .string()
      .nullable()
      .test('notNegativeValorIs', 'O valor do IS não pode ser maior que o valor de venda.', function() {
        return isRetencaoValorValida(formik.values?.tipoIs, formik.values?.isRetidoNaFonte, formik.values?.valorIs);
      }),
    cstSituacaoTributaria: yup.object().nullable(),
    cstClassificacaoTributaria: yup.object().nullable(),
  });

  var formik = useFormik({
    initialValues,
    onSubmit: onSubmit,
    validationSchema: RetencaoAliquotaSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { errors, touched, submitCount } = formik;

  const _errors = {
    valorIss: (submitCount > 0 || touched?.valorIss) && errors?.valorIss,
    valorIrrf: (submitCount > 0 || touched?.valorIrrf) && errors?.valorIrrf,
    valorPis: (submitCount > 0 || touched?.valorPis) && errors?.valorPis,
    valorCofins: (submitCount > 0 || touched?.valorCofins) && errors?.valorCofins,
    valorInss: (submitCount > 0 || touched?.valorInss) && errors?.valorInss,
    valorCsll: (submitCount > 0 || touched?.valorCsll) && errors?.valorCsll,
    cstSituacaoTributaria: (submitCount > 0 || touched?.cstSituacaoTributaria) && errors?.cstSituacaoTributaria,
    cstClassificacaoTributaria:
      (submitCount > 0 || touched?.cstClassificacaoTributaria) && errors?.cstClassificacaoTributaria,
  };

  const close = () => {
    formik.setValues({});
    setModalRetencaoAliquota('');
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  useEffect(() => {
    if (modalRetencaoAliquota === 'retencaoAliquota') {
      formik.resetForm({ values: buildInitialValues() });
    }
    // Efeito reativo apenas ao gatilho acima; incluir as demais reiniciaria o formulário durante a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalRetencaoAliquota, itemServico, produtoServico]);

  return (
    <>
      <SimpleDialog fullScreen open={modalRetencaoAliquota === 'retencaoAliquota'} onClose={handleClose} innerScroll>
        <FormikProvider value={formik}>
          <FormDialogContent
            onSubmit={(e) => {
              formik.handleSubmit();
            }}
            fullScreen
            header={{
              title: 'Retenção de alíquotas',
              subtitle: 'Ajuste as alíquotas que serão exceções na prestação do serviço',
              icon: <MdSettings />,
              toolbar: (
                <>
                  <CancelButton title="cancelar" type="button" onClick={handleClose}>
                    Cancelar
                  </CancelButton>
                  <SaveButton color="buttonSave" type="submit" styles={undefined} variant={'contained'}>
                    Salvar
                  </SaveButton>
                </>
              ),
            }}
            content={
              <>
                <div className="h-full max-h-full w-full">
                  <Grid container>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <label>
                        <b>Nome do serviço</b>
                      </label>
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <label>
                        <b>Código do serviço (SKU)</b>
                      </label>
                    </Grid>
                    <Grid item lg={6} md={6} sm={6} xs={6}>
                      <label>
                        <b>Descrição detalhada do serviço</b>
                      </label>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      {origemVenda
                        ? produtoServico?.produtoServico?.nome
                          ? produtoServico?.produtoServico?.nome
                          : '-'
                        : itemServico?.servico?.descricao
                        ? itemServico.servico.descricao
                        : '-'}
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      {origemVenda
                        ? produtoServico?.produtoServico?.codigo
                          ? produtoServico?.produtoServico?.codigo
                          : '-'
                        : itemServico?.servico?.codigo
                        ? itemServico.servico.codigo
                        : '-'}
                    </Grid>
                    <Grid item lg={6} md={6} sm={6} xs={6}>
                      {origemVenda
                        ? produtoServico?.produtoServico?.descricaoDetalhada
                          ? produtoServico?.produtoServico?.descricaoDetalhada
                          : '-'
                        : itemServico?.servico?.descricaoDetalhada
                        ? itemServico.servico.descricaoDetalhada
                        : '-'}
                    </Grid>
                  </Grid>
                  <TabDadosFiscaisServico errors={_errors} />
                </div>
              </>
            }
          />
        </FormikProvider>
      </SimpleDialog>
    </>
  );
};

export default RetencaoAliquotaModal;
